import React, { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Autocomplete,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import LabelIcon from "@mui/icons-material/Label";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useTagTypes, useTagValues } from "../../../api/tags/tags";
import { ViewAllTagsModal } from "./ViewAllTag";

export function TagFilter({ value, onChange }) {
  const { data: types = [], isLoading: loadingTypes } = useTagTypes();
  const { data: tagValues = [], isLoading: loadingValues } = useTagValues();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [modalOpen, setModalOpen] = useState({ type: false, value: false });

  const TAG_MODAL_LIMIT = 2;

  const typeMap = useMemo(() => {
    const m = {};
    types.forEach((t) => (m[t.id] = t.name));
    return m;
  }, [types]);

  const filteredValues = useMemo(
    () =>
      !value?.types?.length
        ? []
        : tagValues.filter((v) => value.types.includes(v.type)),
    [tagValues, value?.types]
  );
  const selectedTypeObjs = useMemo(
    () => types.filter((t) => value?.types?.includes(t.id)),
    [types, value?.types]
  );
  const selectedValueObjs = useMemo(
    () => tagValues.filter((v) => value?.values?.includes(v.id)),
    [tagValues, value?.values]
  );

  const handleRemoveType = (tag) =>
    onChange({
      ...value,
      types: value.types.filter((id) => id !== tag.id),
      values: value.values.filter((valId) => {
        const v = tagValues.find((val) => val.id === valId);
        return v?.type !== tag.id;
      }),
    });
  const handleRemoveValue = (tag) =>
    onChange({
      ...value,
      values: value.values.filter((id) => id !== tag.id),
    });
  const handleClear = () => onChange({ types: [], values: [] });

  const typeSummary = selectedTypeObjs.length
    ? `${selectedTypeObjs.length} selected`
    : "";
  const valueSummary = selectedValueObjs.length
    ? `${selectedValueObjs.length} selected`
    : "";

  return (
    <>
      <Card elevation={3} sx={{ mb: 1, borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={isMobile ? 2 : 2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Autocomplete
                multiple
                options={types}
                getOptionLabel={(option) => option.name}
                value={selectedTypeObjs}
                onChange={(_, selected) =>
                  onChange({
                    ...value,
                    types: selected.map((t) => t.id),
                    values:
                      value?.values?.filter((v) =>
                        selected
                          .map((t) => t.id)
                          .includes(tagValues.find((val) => val.id === v)?.type)
                      ) || [],
                  })
                }
                loading={loadingTypes}
                disableCloseOnSelect
                noOptionsText="No tag types found"
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Tag Type(s)"
                    placeholder="Choose tag types"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <FilterAltIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "primary.main" }}
                          />
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingTypes ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    value={typeSummary}
                    // helperText="Choose tag categories to filter cases"
                    sx={{
                      minWidth: 250,
                      maxWidth: 320,
                      width: "100%",
                      minHeight: 40,
                    }}
                  />
                )}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  mt: 1,
                  minHeight: 32,
                  pb: 0.5,
                  maxWidth: "100%",
                  "&::-webkit-scrollbar": { height: 8 },
                }}
              >
                {selectedTypeObjs.slice(0, TAG_MODAL_LIMIT).map((tag) => (
                  <Chip
                    key={tag.id}
                    label={<b>{`Type: ${tag.name}`}</b>}
                    color="primary"
                    size="small"
                    variant="soft"
                    onDelete={() => handleRemoveType(tag)}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#e3f2fd",
                      color: "#1565c0",
                      borderRadius: 2,
                      fontSize: 14,
                      height: 28,
                      mr: 0.5,
                      mb: 0.5,
                    }}
                  />
                ))}
                {selectedTypeObjs.length > TAG_MODAL_LIMIT && (
                  <Chip
                    key="view-all-type"
                    icon={<VisibilityIcon />}
                    label={`View All (${selectedTypeObjs.length})`}
                    color="info"
                    size="small"
                    onClick={() => setModalOpen((m) => ({ ...m, type: true }))}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 2,
                      fontSize: 14,
                      height: 28,
                      mr: 0.5,
                      mb: 0.5,
                      bgcolor: "#e1f5fe",
                      color: "#0288d1",
                      cursor: "pointer",
                    }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete
                multiple
                options={filteredValues}
                groupBy={(option) => typeMap[option.type] || "Other"}
                getOptionLabel={(option) => option.name}
                value={selectedValueObjs}
                onChange={(_, selected) =>
                  onChange({
                    ...value,
                    values: selected.map((v) => v.id),
                  })
                }
                loading={loadingValues}
                noOptionsText={
                  value?.types?.length
                    ? "No tag values for selected types"
                    : "Select tag type(s) first"
                }
                disabled={!value?.types?.length}
                disableCloseOnSelect
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Tag(s)"
                    placeholder="Choose tags"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <LabelIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "secondary.main" }}
                          />
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingValues ? (
                            <CircularProgress color="secondary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    value={valueSummary}
                    // helperText="Choose tag values for your selected categories"
                    sx={{
                      minWidth: 250,
                      maxWidth: 320,
                      width: "100%",
                      minHeight: 40,
                    }}
                  />
                )}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  mt: 1,
                  minHeight: 32,
                  pb: 0.5,
                  maxWidth: "100%",
                  "&::-webkit-scrollbar": { height: 8 },
                }}
              >
                {selectedValueObjs.slice(0, TAG_MODAL_LIMIT).map((tag) => (
                  <Chip
                    key={tag.id}
                    label={
                      <b>{`${typeMap[tag.type] || "Type"}: ${tag.name}`}</b>
                    }
                    color="secondary"
                    size="small"
                    variant="soft"
                    onDelete={() => handleRemoveValue(tag)}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      fontWeight: 700,
                      bgcolor: "#ede7f6",
                      color: "#4527a0",
                      borderRadius: 2,
                      fontSize: 14,
                      height: 28,
                      mr: 0.5,
                      mb: 0.5,
                    }}
                  />
                ))}
                {selectedValueObjs.length > TAG_MODAL_LIMIT && (
                  <Chip
                    key="view-all-value"
                    icon={<VisibilityIcon />}
                    label={`View All (${selectedValueObjs.length})`}
                    color="info"
                    size="small"
                    onClick={() => setModalOpen((m) => ({ ...m, value: true }))}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 2,
                      fontSize: 14,
                      height: 28,
                      mr: 0.5,
                      mb: 0.5,
                      bgcolor: "#e1f5fe",
                      color: "#0288d1",
                      cursor: "pointer",
                    }}
                  />
                )}
              </Box>
            </Grid>
            {/* <Grid item justifyContent={"flex-end"} xs={12} md={2}>
              <Button
                onClick={handleClear}
                variant="outlined"
                color="primary"
                size="medium"
              >
                Clear Filters
              </Button>
            </Grid> */}
          </Grid>
        </CardContent>
      </Card>
      <ViewAllTagsModal
        open={modalOpen.type}
        onClose={() => setModalOpen((m) => ({ ...m, type: false }))}
        tags={selectedTypeObjs}
        onRemoveTag={handleRemoveType}
        onClearAll={handleClear}
        title="All Selected Tag Types"
      />
      <ViewAllTagsModal
        open={modalOpen.value}
        onClose={() => setModalOpen((m) => ({ ...m, value: false }))}
        tags={selectedValueObjs}
        typeMap={typeMap}
        onRemoveTag={handleRemoveValue}
        onClearAll={handleClear}
        title="All Selected Tag Values"
      />
    </>
  );
}
